import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import moment from "moment";

import Loader from "@jumbo/components/Loader";
import ReportTables from "./ReportTables";
import { platformLabel } from "./constants";
import { useQuantityEdits } from "./useQuantityEdits";
import { useEditMode } from "./useEditMode";
import BusyOverlay from "./BusyOverlay";

/**
 * A saved import opens here rather than inline, so a preview being reviewed and
 * a stored import can never sit on screen at the same time.
 */
const ImportDetailDrawer = ({
  open,
  loading,
  saving,
  detail,
  pdfLoading,
  canUpdate = false,
  onClose,
  onSaveQuantities,
  onDownloadPdf,
}) => {
  const history = detail?.history;
  const editMode = useEditMode();
  const { editing } = editMode;
  const edits = useQuantityEdits();

  /**
   * Pending edits belong to the report currently on screen, so they are cleared
   * whenever a new one arrives - opening another import, and just as
   * importantly the refetch after a save, which would otherwise leave the
   * "not saved yet" banner up for changes that are already stored.
   */
  useEffect(() => {
    editMode.reset();
    edits.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, open]);

  const parsedRows = history?.totalRows ?? 0;

  // Orders with no chart column make the tables incomplete, so editing is held
  // back until every size belongs to a chart.
  const blockedByExcluded = (detail?.summary?.excludedRows ?? 0) > 0;

  /**
   * Once the stock has gone out, the quantities are what those inventory
   * entries were written from, so editing them would leave the two disagreeing
   * with no way to tell. Undo puts editing back.
   */
  const stockedOut = history?.stockOutStatus === "done";

  const editDisabled = blockedByExcluded || stockedOut || !canUpdate;
  const blockedReason = blockedByExcluded
    ? `${detail.summary.excludedRows} order(s) have no size chart. Add their sizes to a size chart to edit this import.`
    : stockedOut
    ? "This import has already been stocked out. Undo the stock out to change its quantities."
    : !canUpdate
    ? "You do not have permission to change an import's quantities."
    : "";

  const adjustedRows = useMemo(() => {
    if (!detail) return 0;
    const allRows = [...(detail.mapped ?? []), ...(detail.unmapped ?? [])];

    // Cells already stored as overrides are part of the report; only the
    // unsaved changes on top of them move the number.
    const current = detail.summary?.adjustedRows ?? parsedRows;
    return edits.overrides.reduce((sum, item) => {
      const shownRow = allRows.find((row) => row.sku === item.sku);
      const shown = shownRow?.sizes?.[item.size] ?? 0;
      return sum + (Number(item.quantity) - shown);
    }, current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, edits.overrides, parsedRows]);

  /**
   * The API replaces the whole override set, so already-stored edits must be
   * sent again alongside the new ones or they would be dropped.
   */
  const buildOverrides = () => {
    const allRows = [...(detail.mapped ?? []), ...(detail.unmapped ?? [])];
    const merged = new Map();

    allRows.forEach((row) => {
      (row.editedSizes ?? []).forEach((size) => {
        merged.set(`${row.sku}||${size}`, {
          sku: row.sku,
          size,
          quantity: row.sizes[size] ?? 0,
        });
      });
    });

    edits.overrides.forEach((item) => {
      merged.set(`${item.sku}||${item.size}`, item);
    });

    return [...merged.values()];
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: "92%" } } }}
    >
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {history?.originalFileName ?? "Import detail"}
            </Typography>
            {history && (
              <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
                <Chip size="small" color="info" label="Saved" />
                <Chip size="small" label={platformLabel(history.platform)} />
                <Chip
                  size="small"
                  label={moment(history.date).format("DD/MM/YYYY HH:mm")}
                />
                <Chip size="small" label={`${history.totalPages} pages`} />
                <Chip size="small" label={`${parsedRows} rows parsed`} />
                {adjustedRows !== parsedRows && (
                  <Chip
                    size="small"
                    color="warning"
                    label={`${adjustedRows} after edits`}
                  />
                )}
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`${history.mappedRows} mapped`}
                />
                <Chip
                  size="small"
                  color={history.unmappedRows ? "error" : "default"}
                  variant="outlined"
                  label={`${history.unmappedRows} unmapped`}
                />
                {history.isEdited && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`Edited by ${history.editedBy?.name ?? "-"}${
                      history.editedAt
                        ? " on " + moment(history.editedAt).format("DD/MM/YYYY")
                        : ""
                    }`}
                  />
                )}
                {history.importedBy?.name && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Imported by ${history.importedBy.name}`}
                  />
                )}
              </Stack>
            )}
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {detail && !loading && (
              <>
                {/* Built on the server from the same report these tables are
                    drawn from, so the file always matches the screen. */}
                <LoadingButton
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  loading={pdfLoading}
                  onClick={onDownloadPdf}
                >
                  PDF
                </LoadingButton>
                {/* Someone who may only read gets the tables and the PDF,
                    and no editor at all. Beyond that the same rules as the
                    preview apply: orders outside every chart leave the tables
                    incomplete, and a stocked-out import is fixed. */}
                {canUpdate && (
                  <>
                    <Tooltip title={blockedReason}>
                      <span>
                        <Button
                          variant={editing ? "contained" : "outlined"}
                          color={editing ? "warning" : "primary"}
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={editMode.toggle}
                          disabled={editMode.switching || editDisabled}
                        >
                          {editing ? "Done Editing" : "Edit"}
                        </Button>
                      </span>
                    </Tooltip>
                    {/* Tied to pending changes, not to the editor being open -
                        otherwise closing the editor hides the only way to
                        save. */}
                    {edits.hasChanges && (
                      <Tooltip title={blockedReason}>
                        <span>
                          <LoadingButton
                            variant="contained"
                            size="small"
                            loading={saving}
                            onClick={() => onSaveQuantities(buildOverrides())}
                            disabled={editDisabled}
                          >
                            Save Changes
                          </LoadingButton>
                        </span>
                      </Tooltip>
                    )}
                  </>
                )}
              </>
            )}
            <IconButton onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        {edits.hasChanges && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <b>{edits.overrides.length} cell(s) changed</b> and not saved yet.
            The PDF said <b>{parsedRows}</b> rows, these tables now total{" "}
            <b>{adjustedRows}</b>. Press <b>Save Changes</b> to keep them.
          </Alert>
        )}

        {loading || !detail ? (
          <Loader />
        ) : (
          <BusyOverlay
            busy={editMode.switching}
            label={editing ? "Closing editor..." : "Opening editor..."}
          >
            <ReportTables
              report={detail}
              editing={editing}
              edits={edits.edits}
              onCellChange={edits.setCell}
              onCellReset={edits.resetCell}
            />
          </BusyOverlay>
        )}
      </Box>
    </Drawer>
  );
};

export default ImportDetailDrawer;
